import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createToolbarEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import { endConference, leaveConference } from '../../../base/conference/actions';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { NOTIFY_CLICK_MODE } from '../../types';
const useStyles = makeStyles()(theme => {
    const danger = theme.palette.hangupButtonBackground || theme.palette.actionDanger || '#E53935';
    return {
        root: {
            backgroundColor: theme.palette.ui02 || '#1C1C1E',
            borderRadius: 'inherit',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '8px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
            width: '100%'
        },
        handle: {
            alignSelf: 'center',
            backgroundColor: theme.palette.ui04 || '#3A3A3C',
            borderRadius: '3px',
            flexShrink: 0,
            height: '4px',
            marginBottom: '4px',
            width: '36px'
        },
        header: {
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            padding: '0 4px 4px',
            textAlign: 'center'
        },
        title: {
            color: theme.palette.text01 || '#FFFFFF',
            fontSize: '1.0625rem',
            fontWeight: 600,
            lineHeight: 1.35,
            margin: 0
        },
        subtitle: {
            color: theme.palette.text02 || '#AEAEB2',
            fontSize: '0.8125rem',
            lineHeight: 1.45,
            margin: 0
        },
        actionGroup: {
            backgroundColor: theme.palette.ui01 || '#2C2C2E',
            borderRadius: '14px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%'
        },
        actionBtn: {
            appearance: 'none',
            background: 'transparent',
            border: 0,
            color: theme.palette.text01 || '#FFFFFF',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '1rem',
            fontWeight: 500,
            height: '52px',
            lineHeight: 1.2,
            padding: '0 16px',
            textAlign: 'center',
            width: '100%',
            '&:active': {
                backgroundColor: theme.palette.ui02 || '#1C1C1E'
            }
        },
        destructiveBtn: {
            appearance: 'none',
            background: 'transparent',
            border: 0,
            color: danger,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '1rem',
            fontWeight: 600,
            height: '52px',
            lineHeight: 1.2,
            padding: '0 16px',
            textAlign: 'center',
            width: '100%',
            '&:active': {
                backgroundColor: theme.palette.ui02 || '#1C1C1E'
            }
        },
        divider: {
            backgroundColor: theme.palette.ui03 || '#3A3A3C',
            flexShrink: 0,
            height: '1px',
            margin: '0 12px',
            width: 'auto'
        }
    };
});
/**
 * 通知外部嵌入方工具栏点击（若配置了 notifyMode）。
 *
 * @param {string} buttonKey - 按钮 key。
 * @param {string|undefined} notifyMode - 通知模式。
 * @returns {boolean} 是否应继续执行默认动作。
 */
function notifyToolbarClick(buttonKey, notifyMode) {
    if (!notifyMode) {
        return true;
    }
    const prevent = notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY;
    APP.API.notifyToolbarButtonClicked(buttonKey, prevent);
    return !prevent;
}
/**
 * 手机/窄屏挂断底部抽屉：Action Sheet（拖拽条 + 标题说明 + 离开/结束操作）。
 * 点遮罩空白处关闭抽屉，不提供独立「取消」按钮。
 * 使用原生 button，避免被通用 Button 组件默认灰底/红底样式盖回旧观感。
 *
 * @param {IProps} props - 组件属性。
 * @returns {ReactElement}
 */
const HangupDrawerContent = ({ buttonsWithNotifyClick, endConferenceSupported, id, onKeyDown }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isModerator = useSelector(isLocalParticipantModerator);
    const inBreakout = useSelector(isInBreakoutRoom);
    const showEndForAll = endConferenceSupported && isModerator && !inBreakout;
    const subtitleKey = showEndForAll
        ? 'toolbar.hangupDrawer.subtitleModerator'
        : 'toolbar.hangupDrawer.subtitleParticipant';
    const onLeave = useCallback(() => {
        if (!notifyToolbarClick('hangup', buttonsWithNotifyClick?.get('hangup'))) {
            return;
        }
        sendAnalytics(createToolbarEvent('hangup'));
        dispatch(leaveConference());
    }, [buttonsWithNotifyClick, dispatch]);
    const onEndAll = useCallback(() => {
        if (!notifyToolbarClick('end-meeting', buttonsWithNotifyClick?.get('end-meeting'))) {
            return;
        }
        dispatch(endConference());
    }, [buttonsWithNotifyClick, dispatch]);
    return (React.createElement("div", { "aria-label": t('toolbar.accessibilityLabel.hangup'), className: classes.root, id: id, onKeyDown: onKeyDown, role: 'menu' },
        React.createElement("div", { "aria-hidden": true, className: classes.handle }),
        React.createElement("header", { className: classes.header },
            React.createElement("h2", { className: classes.title }, t('toolbar.hangupDrawer.title')),
            React.createElement("p", { className: classes.subtitle }, t(subtitleKey))),
        React.createElement("div", { className: classes.actionGroup },
            React.createElement("button", { "aria-label": t('toolbar.accessibilityLabel.leaveConference'), className: classes.actionBtn, onClick: onLeave, role: 'menuitem', type: 'button' }, t('toolbar.leaveConference')),
            showEndForAll && (React.createElement(React.Fragment, null,
                React.createElement("div", { "aria-hidden": true, className: classes.divider }),
                React.createElement("button", { "aria-label": t('toolbar.accessibilityLabel.endConference'), className: classes.destructiveBtn, onClick: onEndAll, role: 'menuitem', type: 'button' }, t('toolbar.endConference')))))));
};
export default HangupDrawerContent;
